import React, { useState } from 'react';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import classnames from 'classnames';
import { gettext } from '@eb/i18n-ebspa';

import { Icon } from '@eb/eds-icon';
import { Button } from '@eb/eds-button';
import { IconButton } from '@eb/eds-icon-button';
import { ArrowLeftChunky as ArrowLeftChunkySvg } from '@eb/eds-iconography';
import { ChevronLeftChunky as ChevronLeftChunkySvg } from '@eb/eds-iconography';

import FilterPanelDrawers from './FilterPanelDrawers';
import Filters from './Filters';

import { FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP } from '../../constants';

import './FilterPanel.scss';

const MULTISELECT_MAP = {
    languages: true,
};

const getFilterPanelTitle = (filterTypeToShow, isDatePickerDrawerVisible) => {
    let filterTitle = gettext('Filters');

    if (isDatePickerDrawerVisible) {
        return gettext('Pick a date...');
    }

    if (filterTypeToShow) {
        filterTitle = get(FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP, [
            filterTypeToShow,
            'label',
        ]);
    }

    return filterTitle;
};

const SaveButton = ({ isActive, onClick }) => (
    <Button
        size="block"
        style="fill"
        onClick={onClick}
        disabled={!isActive}
        data-integration="filter-panel-mobile--save-btn"
    >
        {gettext('Save')}
    </Button>
);

const FilterPanelHeader = ({
    filterTitle,
    filterTypeToShow,
    isDatePickerDrawerVisible,
    onCloseDatePickerDrawer,
    handleDrawerSelect,
    children,
    isMobile,
}) => {
    let headerAction = null;
    let newAction = null;

    if (isDatePickerDrawerVisible) {
        headerAction = (
            <IconButton
                data-integration="filter-title__drawer-close-btn"
                iconType={<ArrowLeftChunkySvg />}
                title="Go back to filter selection"
                onClick={() => onCloseDatePickerDrawer(false)}
            />
        );
    }
    // Back button when a drawer is selected
    if (filterTypeToShow && !isDatePickerDrawerVisible) {
        newAction = (
            <Button
                style="none"
                onClick={() => {
                    handleDrawerSelect('');
                    if (isDatePickerDrawerVisible) {
                        onCloseDatePickerDrawer(false);
                    }
                }}
            >
                <Icon
                    type={<ChevronLeftChunkySvg />}
                    size="xsmall"
                    color="grey-700"
                />
                {gettext('Filters')}
            </Button>
        );
    }

    const classes = {
        container: `filter-title-container ${
            isMobile ? 'filter-title-container--mobile' : ''
        }`,
        title: `filter-title ${isMobile ? 'filter-title--mobile' : ''}`,
    };

    return (
        <header className={classes.container}>
            {headerAction}
            <h3
                className={classes.title}
                data-integration="filter-title--mobile"
            >
                <div className="filter-title__filter-close">{newAction}</div>
                <span data-integration="filter-title--mobile__header">
                    {filterTitle}
                </span>
            </h3>
            {children}
        </header>
    );
};

const FilterPanelMain = (props) => (
    <div
        className={classnames('filter-panel', {
            'filter-panel--mobile': props.isMobile,
        })}
        data-integration={
            props.isMobile ? 'filter-panel--mobile' : 'filter-panel'
        }
    >
        {props.children}
    </div>
);

const FilterPanelFooter = (props) => {
    if (props.children.every((child) => !child === true)) {
        return null;
    }

    return (
        <div
            className={`filter-footer__container ${
                props.isMobile ? 'filter-footer__container--mobile' : ''
            }`}
        >
            <div
                className={`filter-footer ${
                    props.isMobile ? 'filter-footer--mobile' : ''
                }`}
            >
                {props.children}
            </div>
        </div>
    );
};

const FilterPanel = (props) => {
    const [isSaveActive, setIsSaveActive] = useState(false);
    const [currentMultiVal, setCurrentMultiVal] = useState({});
    const [isDatePickerDrawerVisible, setDatePickerDrawerVisible] = useState(
        false,
    );

    const handleCustomDateChange = (...args) => {
        setDatePickerDrawerVisible(false);
        props.handleDrawerSelect('');
        props.onCustomDateChange(...args);
    };

    const handleMultiSelectSave = () => {
        const { key, shouldUpdateRoute, value } = currentMultiVal[
            props.filterTypeToShow
        ];

        props.onFilterChange(key, value, shouldUpdateRoute);
        setIsSaveActive(false);
        setCurrentMultiVal({});
    };

    const handleFilterChange = (key, val, shouldUpdateRoute) => {
        if (MULTISELECT_MAP[key]) {
            setCurrentMultiVal({
                [key]: {
                    key,
                    shouldUpdateRoute,
                    value: val,
                },
            });

            setIsSaveActive(!isEqual(props.selectedFilters[key], val));
        } else {
            props.onFilterChange(key, val, shouldUpdateRoute);
        }
    };

    const headerTitle = getFilterPanelTitle(
        props.filterTypeToShow,
        isDatePickerDrawerVisible,
    );

    /* When the user has selected a type of filter,
     show the filters for that selected type.  */
    const Content = props.filterTypeToShow ? Filters : FilterPanelDrawers;

    return (
        <div
            className={classnames('filter-panel-and-title-container', {
                'filter-panel-and-title-container--mobile': props.isMobile,
            })}
        >
            <FilterPanelHeader
                isMobile={props.isMobile}
                filterTitle={headerTitle}
                filterTypeToShow={props.filterTypeToShow}
                isDatePickerDrawerVisible={isDatePickerDrawerVisible}
                onCloseDatePickerDrawer={setDatePickerDrawerVisible}
                handleDrawerSelect={props.handleDrawerSelect}
            >
                {props.renderPanelHeaderAction &&
                    props.renderPanelHeaderAction()}
            </FilterPanelHeader>
            <FilterPanelMain isMobile={props.isMobile}>
                <Content
                    {...props}
                    onFilterChange={handleFilterChange}
                    onCustomDateChange={handleCustomDateChange}
                    isDatePickerDrawerVisible={isDatePickerDrawerVisible}
                    setDatePickerDrawerVisible={(val) =>
                        setDatePickerDrawerVisible(val)
                    }
                />
            </FilterPanelMain>
            <FilterPanelFooter isMobile={props.isMobile}>
                {props.filterTypeToShow === 'languages' && (
                    <SaveButton
                        isActive={isSaveActive}
                        onClick={handleMultiSelectSave}
                    />
                )}
                {props.stickyFooter && props.stickyFooter}
            </FilterPanelFooter>
        </div>
    );
};

export default FilterPanel;
